import { permissionApi } from '../../api/permissionApi'
import { userApi } from "../../api/userApi"

import { currentBusinessEntitySignal, businessEntitiesLoadedSignal, ancestorBEIDsSetSignal } from '../signals/businessEntitySignals.js'
import { permissionDefinitionsSignal, permissionDefinitionsLoadedSignal, permissionDefinitionsErrorSignal, userPermissionMasksSignal, userPermissionMasksLoadedSignal, userPermissionMasksErrorSignal, userPermissionsSignal, userPermissionsSummarySignal } from '../signals/permissionSignals.js'

import { hasPermission } from '../../utils/utils'
import { UserPermissions } from '../../utils/constants'

const unmaskPermission = (mask) => {
    if (!permissionDefinitionsLoadedSignal.value || !permissionDefinitionsSignal.value) {
        throw new Error("Permission definitions have not loaded")
    }
    // remove some fields that aren't helpful
    delete mask.userId; // not needed since we have a user object
    delete mask.beList; // this is always null
    // augment with the actual permission name and key
    const p = permissionDefinitionsSignal.value.find(
        (element) => {
            if (!("id" in element)) {
                console.error("Permission definition lacks an ID property")
                console.log(element)
                return false
            }
            return element.id == mask.permissionId
        }
    )
    if (p) {
        mask.name = p.name
        mask.key = p.key
    }
    // augment with flags rather than mask
    mask.canView = (16 & mask.levelMask) === 16
    mask.canEdit = (8 & mask.levelMask) === 8
    mask.canDelete = (4 & mask.levelMask) === 4
    mask.isEnabled = (2 & mask.levelMask) === 2
    mask.canGrant = (1 & mask.levelMask) === 1
    return mask
}

export const generateUserPermissions = () => {
    if (!businessEntitiesLoadedSignal.value || !currentBusinessEntitySignal.value) {
        throw new Error("Cannot generate user permissions until the current business entity has loaded")
    }
    if (!permissionDefinitionsLoadedSignal.value) {
        throw new Error("Cannot generate user permissions until their definitions have been loaded")
    }
    // walk up the business entity ancestry to get a list of all be ids
    let ancestorBEIds = [...ancestorBEIDsSetSignal.value]
    let current = currentBusinessEntitySignal.value
    // handle if assigned to shift4 root
    if (current) {
        ancestorBEIds.unshift(current.id)
    }
    /*while (current && current.parentId != 0) {
        current = current.parentBusinessEntity
        if (current) {
            ancestorBEIds.push(current.id)
        }
    }*/
    //console.log("List of ancestor BEIds including the current one:")
    //console.log(ancestorBEIds.join(", "))
    // loop over all permission definitions and build a Map indexed by permission key. The value will be an object with default access flags
    const permissionsMap = new Map()
    permissionDefinitionsSignal.value.forEach(p => {
        // default to denied
        let userPermission = {
            id: p.id,
            key: p.key,
            name: p.name,
            description: p.description,
            canView: false,
            canEdit: false,
            canDelete: false,
            isEnabled: false,
            canGrant: false
        }
        let current = permissionsMap.get(p.key)
        userPermission = current ? current : userPermission
        // add/update the Map
        permissionsMap.set(p.key, userPermission)
    })
    // update permissionsMap as needed to reflect permissions that are enabled
    permissionsMap.forEach(p => {
        const masks = userPermissionMasksSignal.value.filter(item => item.permissionId == p.id)
        masks.forEach(m => {
            ancestorBEIds.forEach(ancestorBEId => {
                if (m.roleBusinessEntityIds.includes(ancestorBEId)) {
                    // the selected be or its ancestor is in the current permissionMask
                    // update the flags in our permissionMask if any are enabled
                    if (m.canView) {
                        p.canView = true
                    }
                    if (m.canEdit) {
                        p.canEdit = true
                    }
                    if (m.canDelete) {
                        p.canDelete = true
                    }
                    if (m.isEnabled) {
                        p.isEnabled = true
                    }
                    if (m.canGrant) {
                        p.canGrant = true
                    }
                }
            })
        })

    })
    userPermissionsSignal.value = [...permissionsMap.values()]
    return userPermissionsSignal.value
}

export const generateUserPermissionsSummary = () => {
    // userPermissionsSummary is a high level permission structure that navigation and most components can use to control the UI visibility. If a component needs more granular permissions
    // it can refer to the userPermissions array which contains all available permissions and actions values for the currently selected business entity
    if (userPermissionsSignal.value.length) {
        let summary = {
            canViewUtgs: (!!userPermissionsSignal.value.length && hasPermission(userPermissionsSignal.value, 'In4m:UtgServices', UserPermissions.canView)),
            canEditUtgs: (!!userPermissionsSignal.value.length && hasPermission(userPermissionsSignal.value, 'In4m:UtgServices', UserPermissions.canEdit)),

            canViewConfigTemplates: (!!userPermissionsSignal.value.length && hasPermission(userPermissionsSignal.value, 'In4m:UtgServices', UserPermissions.canView)),
            canEditConfigTemplates: (!!userPermissionsSignal.value.length && hasPermission(userPermissionsSignal.value, 'In4m:UtgServices', UserPermissions.canEdit)),

            canViewTasks: (!!userPermissionsSignal.value.length && hasPermission(userPermissionsSignal.value, 'In4m:Machines', UserPermissions.canView)),
            canEditTasks: (!!userPermissionsSignal.value.length && hasPermission(userPermissionsSignal.value, 'In4m:Machines', UserPermissions.canEdit)),

            canViewDevices: (!!userPermissionsSignal.value.length && hasPermission(userPermissionsSignal.value, 'In4m:Devices', UserPermissions.canView)),
            canEditDevices: (!!userPermissionsSignal.value.length && hasPermission(userPermissionsSignal.value, 'In4m:Devices', UserPermissions.canEdit)),

            canViewForms: (!!userPermissionsSignal.value.length && hasPermission(userPermissionsSignal.value, 'In4m:DeviceForms', UserPermissions.canView)),
            canEditForms: (!!userPermissionsSignal.value.length && hasPermission(userPermissionsSignal.value, 'In4m:DeviceForms', UserPermissions.canEdit)),

            canViewProfiles: (!!userPermissionsSignal.value.length && hasPermission(userPermissionsSignal.value, 'In4m:UtgConfigurations', UserPermissions.canView)),
            canEditProfiles: (!!userPermissionsSignal.value.length && hasPermission(userPermissionsSignal.value, 'In4m:UtgConfigurations', UserPermissions.canEdit)),

            canViewInstallations: (!!userPermissionsSignal.value.length && hasPermission(userPermissionsSignal.value, 'In4m:UtgInstallations', UserPermissions.canView)),
            canEditInstallations: (!!userPermissionsSignal.value.length && hasPermission(userPermissionsSignal.value, 'In4m:UtgInstallations', UserPermissions.canEdit)),

            canViewUsers: (!!userPermissionsSignal.value.length && hasPermission(userPermissionsSignal.value, 'Users', UserPermissions.canView)),
            canEditUsers: (!!userPermissionsSignal.value.length && hasPermission(userPermissionsSignal.value, 'Users', UserPermissions.canEdit)),

            canViewGroups: (!!userPermissionsSignal.value.length && hasPermission(userPermissionsSignal.value, 'Groups', UserPermissions.canView)),
            canEditGroups: (!!userPermissionsSignal.value.length && hasPermission(userPermissionsSignal.value, 'Groups', UserPermissions.canEdit)),

            canViewRoles: (!!userPermissionsSignal.value.length && hasPermission(userPermissionsSignal.value, 'Roles', UserPermissions.canView)),
            canEditRoles: (!!userPermissionsSignal.value.length && hasPermission(userPermissionsSignal.value, 'Roles', UserPermissions.canEdit)),

            canViewOrganizations: (!!userPermissionsSignal.value.length && hasPermission(userPermissionsSignal.value, 'Organizations', UserPermissions.canView)),
            canEditOrganizations: (!!userPermissionsSignal.value.length && hasPermission(userPermissionsSignal.value, 'Organizations', UserPermissions.canEdit)),

            canViewSystemUsers: (!!userPermissionsSignal.value.length && hasPermission(userPermissionsSignal.value, '4tress:SystemUsers', UserPermissions.canView)),
            canViewEphemeralUsers: (!!userPermissionsSignal.value.length && hasPermission(userPermissionsSignal.value, '4tress:EphemeralUsers', UserPermissions.canView))
        }
        //DEBUG
        // overwrite the correct values for debugging page access
        // summary.canViewUtgs = true
        // summary.canEditUtgs = false
        // summary.canViewConfigTemplates = true
        // summary.canEditConfigTemplates = true
        // summary.canViewTasks = true
        // summary.canEditTasks = true
        // summary.canViewDevices = true
        // summary.canEditDevices = true
        // summary.canViewForms = true
        // summary.canEditForms = true
        // summary.canViewProfiles = true
        // summary.canEditProfiles = true
        // summary.canViewInstallations = true
        // summary.canEditInstallations = true
        // summary.canViewUsers = true
        // summary.canEditUsers = false
        // summary.canViewRoles = true
        // summary.canEditRoles = true
        // console.log("User permissions summary:")
        // console.log(summary)
        userPermissionsSummarySignal.value = summary

        //console.log("User permissions:")
        //console.log(userPermissionsSignal.value)
        //console.log("User permissions summary:")
        //console.log(summary)

        return summary
    }
}

export const fetchPermissionDefinitions = async (config, accessToken) => {
    if (permissionDefinitionsErrorSignal.value) {
        console.error(permissionDefinitionsErrorSignal.value)
        return null
    }
    permissionDefinitionsLoadedSignal.value = false
    permissionDefinitionsSignal.value = []
    return await permissionApi.fetchPermissionDefinitions(config, accessToken).then(
        (permissionDefinitions) => {
            permissionDefinitionsLoadedSignal.value = true
            return permissionDefinitionsSignal.value = permissionDefinitions
        }
    ).catch(
        (error) => {
            permissionDefinitionsErrorSignal.value = error
            throw error
        }
    )
}

export const fetchUserPermissionMasks = async(userId, config, accessToken, debug = false) => {
    const f = "fetchUserPermissionMasks()"
    let print = false
    if (!debug) {
        userPermissionMasksLoadedSignal.value = false
        userPermissionMasksSignal.value = []
        userPermissionsSignal.value = []
        userPermissionsSummarySignal.value = null
    }
    return await userApi.fetchUserPermissionMasks(userId, config, accessToken).then(
        (userPermissionMasks) => {
            if (debug) {
                return userPermissionMasks
            }
            let processedMasks = []
            for (let i = 0; i < userPermissionMasks.length; i++) {
                const mask = userPermissionMasks[i]
                const unmasked = unmaskPermission(mask)
                processedMasks.push(unmasked)
            }
            userPermissionMasksSignal.value = processedMasks
            if (currentBusinessEntitySignal.value) {
                generateUserPermissions()
                generateUserPermissionsSummary()
            }
            else if (print) {
                console.log("Current business entity is not loaded, skipping user permission generation")
            }
            userPermissionMasksLoadedSignal.value = true
            return userPermissionMasksSignal.value
        }
    ).catch(
        (x) => {
            userPermissionMasksErrorSignal.value = x
            console.error("Caught an error in fetchUserPermissionMasks: ".concat(x.toString()))
            throw x
        }
    )
}